import type { ColumnTypeAssignment } from './columnType';

export type FileFormat = 'csv' | 'xlsx' | 'json' | 'parquet';

export type ImportSessionStatus =
  | 'pending'
  | 'uploading'
  | 'parsing'
  | 'complete'
  | 'failed'
  | 'cancelled';

export type ImportErrorType =
  | 'unsupported_format'
  | 'file_too_large'
  | 'empty_file'
  | 'parse_failure'
  | 'encoding_error'
  | 'nesting_too_deep'
  | 'session_active';

export interface ImportError {
  error_type: ImportErrorType;
  message: string;
  guidance: string;
}

export interface ImportSession {
  id: string;
  workspace_id: string;
  status: ImportSessionStatus;
  progress_percent: number;
  filename: string;
  file_size_bytes: number;
  bytes_uploaded: number;
  error_type: string | null;
  error_message: string | null;
  error_guidance: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface ImportWarning {
  type: string;
  message: string;
  affected_rows: number[];
}

export interface DatasetRow {
  row_index: number;
  values: (string | number | null)[];
}

export interface DatasetWithPreview {
  id: string;
  workspace_id: string;
  filename: string;
  file_format: FileFormat;
  columns: string[];
  row_count: number;
  column_count: number;
  imported_at: string;
  import_warnings: ImportWarning[] | null;
  preview_rows: DatasetRow[];
  column_types?: ColumnTypeAssignment[] | null;
}

export interface DatasetSummary {
  id: string;
  filename: string;
  file_format: FileFormat;
  row_count: number;
  column_count: number;
  imported_at: string;
}

export interface ChunkUploadResponse {
  session_id: string;
  progress_percent: number;
  status: 'uploading' | 'parsing';
}

export interface ExistingDatasetConflict {
  message: string;
  existing_dataset: {
    id: string;
    filename: string;
    row_count: number;
    imported_at: string;
  };
  confirm_url: string;
}

/** Helper to extract ImportError from a flat ImportSession response */
export function extractImportError(session: ImportSession): ImportError | null {
  if (session.error_type && session.error_message && session.error_guidance) {
    return {
      error_type: session.error_type as ImportErrorType,
      message: session.error_message,
      guidance: session.error_guidance,
    };
  }
  return null;
}
